'use client';

import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '@/lib/utils';
import type { Transaction } from '@/lib/types';
import React from 'react';

interface IncomeExpenseChartProps {
    transactions: Transaction[];
}

export function IncomeExpenseChart({ transactions }: IncomeExpenseChartProps) {
    const data = React.useMemo(() => {
        const monthData: { [month: string]: { income: number; expenses: number } } = {};
        
        transactions.forEach(t => {
            const month = new Date(t.date).toLocaleString('default', { month: 'short' });
            if (!monthData[month]) {
                monthData[month] = { income: 0, expenses: 0 };
            }
            if (t.type === 'income') {
                monthData[month].income += Number(t.amount) || 0;
            } else {
                monthData[month].expenses += Number(t.amount) || 0;
            }
        });
        
        return Object.entries(monthData).map(([month, values]) => ({ month, ...values }));
    }, [transactions]);

    return (
        <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} dy={8} />
                    <YAxis
                        tickFormatter={(v) => formatCurrency(v as number)}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11 }}
                        width={72}
                    />
                    <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                        cursor={{ fill: 'hsl(var(--muted))' }}
                        contentStyle={{
                            borderRadius: '12px',
                            border: '1px solid hsl(var(--border))',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                            padding: '8px 12px',
                        }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '16px', fontSize: '13px' }} />
                    <Bar dataKey="income" name="Income" fill="hsl(var(--chart-2))" radius={[6, 6, 0, 0]} maxBarSize={48} />
                    <Bar dataKey="expenses" name="Expenses" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
